import { useMutation, useQuery } from '@apollo/client';
import { useAccount, useLogin } from '@bnnvara/accounts';

import { track } from '@bnnvara/analytics/src/track';
import { useBrand } from '@bnnvara/brand';
import {
  CommentBox,
  CommentsComponent,
  sortNewestFirst,
  sortOldestFirst,
  sortRating,
} from '@bnnvara/comments';
import { number, string, shape } from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { useRouter } from 'next/router';
import { CREATE_COMMENT } from '../../api/mutations/comment.gql';
import reportComment from '../../api/mutations/reportCommentMutation.gql';
import voteComment from '../../api/mutations/voteCommentMutation.gql';
import FETCH_COMMENTS from '../../api/queries/comments.gql';
import { useFingerprint } from '../../hooks/fingerprint';
import { utmParams } from '../../helpers/utmParams';
import useScrollToElement from '../../hooks/useScrollToElement';
import { stripQueryParams } from '../../helpers/stripQueryParams';
import OptimisticObjFactory from './Graphql/OptimisticObjFactory';
import CacheUpdater from './Graphql/CacheUpdater';

const Comments = ({
  contentId,
  contentType,
  commentsEnabledByPage,
  commentCount,
  heading,
}) => {
  // HOOKS
  const { slug: brandSlug, id: brandId, commentSetting } = useBrand();
  const { asPath } = useRouter();
  const { isLoggedIn, logout, login } = useLogin();
  const { profile, token } = useAccount();
  const scrollToRef = useRef(null);
  const fingerprint = useFingerprint();

  // Don't scroll when SSR or there is no "reageren" hash in the url
  const skipScroll = !process.browser || document.location.hash !== '#reageren';
  useScrollToElement(scrollToRef, false, skipScroll);

  // STATES
  const [errorMessageIsOpen, setErrorMessageIsOpen] = useState(false);
  const [isMessageSend, setMessageSend] = useState(false);
  const [orderDirection, setOrderDirection] = useState('DESC');
  const [orderField, setOrderField] = useState('createdAt');
  const [isReportMessageSend, setReportMessageSend] = useState(false);
  const [sortOption, setSortOption] = useState(sortNewestFirst);

  // VARIABLES
  const brandName = brandSlug.charAt(0).toUpperCase() + brandSlug.slice(1);
  const reportUrl = process.browser ? window.location.href : '';
  const commentsEnabledByBrand = {
    DISABLED: 0,
    PREMODERATION: 1,
    POSTMODERATION: 2,
  };
  const hideComments =
    commentSetting === commentsEnabledByBrand.DISABLED ||
    commentsEnabledByPage === 'false';

  const showCommentReport = true;
  const utm = utmParams();

  // MUTATIONS && QUERIES
  const [createOpinion, sendReport] = useMutation(reportComment);
  const [createVote] = useMutation(voteComment);
  const [upsertComment, sendResponse] = useMutation(CREATE_COMMENT);
  const { loading, error, data } = useQuery(FETCH_COMMENTS, {
    variables: {
      contentId,
      activeUserId: profile.UID || null,
      orderField,
      orderDirection,
    },
    skip: hideComments,
  });

  useEffect(() => {
    if (sendResponse?.data?.addComment) {
      setMessageSend(true);
      setErrorMessageIsOpen(false);
    }
    if (sendReport?.data?.reportComment) {
      setReportMessageSend(true);
    }
  }, [sendReport.data, sendResponse]);

  // FUNCTIONS
  const openLogin = (e) => {
    e.preventDefault();
    track('comments', 'click', 'login-registreer-comments');
    return login();
  };
  const selectOrder = (value) => {
    switch (value) {
      case sortOldestFirst:
        setOrderField('createdAt');
        setOrderDirection('ASC');
        setSortOption(sortOldestFirst);
        break;
      case sortRating:
        setOrderField('opinionPositiveCount');
        setOrderDirection('DESC');
        setSortOption(sortRating);
        break;
      default:
        setOrderField('createdAt');
        setOrderDirection('DESC');
        setSortOption(sortNewestFirst);
    }
  };
  const sendMutation = async (getCommentValue, referenceId, commentType) => {
    if (commentType === 'comment') {
      const { data: upsertCommentData } = await upsertComment({
        variables: {
          contentId: referenceId,
          avatarUrl: profile.profilePicture
            ? `${profile.profilePicture}?d=240x240`
            : '',
          body: getCommentValue,
          contentType,
          brandId,
          slug: asPath.split('?')[0],
          utm,
        },
        update(cache, { data: { createComment: newComments } }) {
          if (newComments && !newComments.error) {
            CacheUpdater(
              cache,
              OptimisticObjFactory(profile, referenceId, getCommentValue)
            );
          }
        },
      });
      track('comments', 'click', `${commentType}-post-comment`);

      /**
       * Return false if a user is blocked
       */
      return upsertCommentData.createComment;
    }
    // mutation for the subcomment
    const { data: newSubComment } = await upsertComment({
      variables: {
        contentId: referenceId,
        avatarUrl: profile.profilePicture
          ? `${profile.profilePicture}?d=240x240`
          : '',
        body: getCommentValue,
        contentType: 'comment',
        brandId,
        slug: stripQueryParams(asPath),
        utm,
      },
      update(cache, { data: { createComment: newComments } }) {
        if (newComments && !newComments.error) {
          const updatedComment = {
            ...data.comments.comments.find((item) => {
              return item.id === referenceId;
            }),
          };
          updatedComment.subComments = [
            ...updatedComment.subComments,
            OptimisticObjFactory(profile, referenceId, getCommentValue),
          ];
          // nice string with int to string conversion
          updatedComment.subCommentCount += 1;

          CacheUpdater(cache, updatedComment);
        }
      },
    });
    track('comments', 'click', `${commentType}-post-comment`);
    /**
     * Return false if a user is blocked
     */
    return newSubComment?.createComment;
  };
  const sendReportMutation = (passedContentId, commentId, commentType) => {
    createOpinion({
      variables: {
        commentId,
        contentId: passedContentId,
        sourceBrandName: brandName,
        sourceUrl: reportUrl,
        avatarUrl: `${profile.profilePicture}?d=240x240` || '',
        type: 'inappropriate',
      },
    });

    track('comments', 'click', `${commentType}-report-comment`);
  };
  const sendVoteMutation = (
    passedContentId,
    commentId,
    voteType,
    commentType,
    parentId = null,
    isPositive = true
  ) => {
    createVote({
      variables: {
        commentId,
        contentId: passedContentId,
        sourceBrandName: brandName,
        sourceUrl: reportUrl,
        avatarUrl: `${profile.profilePicture}?d=240x240` || '',
        type: 'positive',
        contentType,
        brandId,
        slug: stripQueryParams(asPath),
        utm,
        fingerprint,
        parentId,
        isPositive,
      },
    });
    track('comments', 'click', `${voteType}-${commentType}`);
  };

  // RENDER
  // based on data
  if (!data || loading || error) {
    return null;
  }

  const { comments } = data;

  return (
    <>
      <span ref={scrollToRef} />
      <CommentBox
        isLoggedIn={isLoggedIn}
        profilePicture={profile.profilePicture}
        openLogin={openLogin}
        nickname={profile.nickname}
        logout={logout}
        isMessageOpen={errorMessageIsOpen}
        isMessageSend={isMessageSend}
        subComment={false}
        commentId={contentId}
        sendMutation={sendMutation}
        heading={heading}
      />
      {comments !== null && comments?.comments?.length >= 1 && (
        <CommentsComponent
          key={contentId}
          data={comments}
          mainCommentsLength={commentCount}
          isLoggedIn={isLoggedIn}
          openLogin={openLogin}
          sendMutation={sendMutation}
          isReportMessageSend={isReportMessageSend}
          sendReportMutation={sendReportMutation}
          sendVoteMutation={sendVoteMutation}
          passedContentId={contentId}
          showCommentReport={showCommentReport}
          isMessageOpen={errorMessageIsOpen}
          isMessageSend={isMessageSend}
          jwtToken={token}
          commentId={contentId}
          selectOrder={selectOrder}
          popularOrder={orderDirection}
          sortOption={sortOption}
        />
      )}
    </>
  );
};

Comments.propTypes = {
  commentCount: number.isRequired,
  contentId: string.isRequired,
  contentType: string.isRequired,
  commentsEnabledByPage: string.isRequired,
  heading: shape({
    title: string.isRequired,
    description: string.isRequired,
  }).isRequired,
};

export default Comments;
